import { get, set, del, keys } from "idb-keyval";
import type { CSVMapping, AccountMeta, BusinessEntity, ExpenseClass, IncomeSource, ClassificationRule, StoredBudgetPlan, FamilyMember } from "./types";
import { generateDedupKey } from "./csv-processor";

const ACCOUNTS_KEY = "csv-accounts";
const MAPPINGS_KEY = "csv-mappings";
const TX_PREFIX = "csv-tx-";
const ACCOUNT_META_KEY = "csv-account-meta";
const ENTITIES_KEY = "csv-entities";
const CLASSES_KEY = "csv-expense-classes";
const INCOME_SOURCES_KEY = "csv-income-sources";
const RULES_KEY = "csv-classification-rules";
const BUDGET_PLAN_KEY = "csv-budget-plan";
const FAMILY_KEY = "csv-family-members";
const CATEGORIES_KEY = "csv-categories"; // per-entity category lists

export async function saveAccount(account: Account): Promise<void> {
  const accounts = await getAllAccounts();
  const existing = accounts.findIndex((a) => a.id === account.id);
  if (existing >= 0) {
    accounts[existing] = account;
  } else {
    accounts.push(account);
  }
  await set(ACCOUNTS_KEY, accounts);
}

export async function getAllAccounts(): Promise<Account[]> {
  return (await get<Account[]>(ACCOUNTS_KEY)) || [];
}

export async function deleteAccount(accountId: string): Promise<void> {
  const accounts = await getAllAccounts();
  const filtered = accounts.filter((a) => a.id !== accountId);
  await set(ACCOUNTS_KEY, filtered);
  await del(TX_PREFIX + accountId);
}

export async function saveTransactions(
  accountId: string,
  transactions: Transaction[]
): Promise<void> {
  await set(TX_PREFIX + accountId, transactions);
}

export async function getTransactions(
  accountId: string
): Promise<Transaction[]> {
  return (await get<Transaction[]>(TX_PREFIX + accountId)) || [];
}

export async function getAllTransactions(): Promise<Transaction[]> {
  const accounts = await getAllAccounts();
  const allTx: Transaction[] = [];
  for (const account of accounts) {
    const tx = await getTransactions(account.id);
    allTx.push(...tx);
  }
  return allTx;
}

/** Remove transactions that already exist (by dedup key) across all accounts */
export async function deduplicateTransactions(
  newTransactions: Transaction[]
): Promise<{ unique: Transaction[]; skipped: number }> {
  const existing = await getAllTransactions();
  const existingKeys = new Set(existing.map(generateDedupKey));
  const unique = newTransactions.filter((tx) => !existingKeys.has(generateDedupKey(tx)));
  return { unique, skipped: newTransactions.length - unique.length };
}

export async function saveMappings(mappings: CSVMapping[]): Promise<void> {
  await set(MAPPINGS_KEY, mappings);
}

export async function getMappings(): Promise<CSVMapping[]> {
  return (await get<CSVMapping[]>(MAPPINGS_KEY)) || [];
}

export async function clearAll(): Promise<void> {
  const allKeys = await keys();
  for (const key of allKeys) {
    if (
      String(key).startsWith(TX_PREFIX) ||
      key === ACCOUNTS_KEY ||
      key === MAPPINGS_KEY
    ) {
      await del(key);
    }
  }
}

export async function hasCSVData(): Promise<boolean> {
  const accounts = await getAllAccounts();
  return accounts.length > 0;
}

// Export/Import all data as JSON (for sharing between environments)

export async function exportAllData(): Promise<string> {
  const accounts = await getAllAccounts();
  const allTx: Record<string, Transaction[]> = {};
  for (const acc of accounts) {
    allTx[acc.id] = await getTransactions(acc.id);
  }
  const mappings = await getMappings();
  const accountMeta = await getAllAccountMeta();
  const entities = await getAllEntities();
  const expenseClasses = await getAllExpenseClasses();
  const incomeSources = await getAllIncomeSources();
  const rules = await getAllRules();
  const budgetPlans = await getAllBudgetPlans();
  const familyMembers = await getAllFamilyMembers();
  const categories = await get<Record<string, string[]>>(CATEGORIES_KEY) || {};

  return JSON.stringify({
    accounts, transactions: allTx, mappings,
    accountMeta, entities, expenseClasses, incomeSources,
    rules, budgetPlans, familyMembers, categories,
  }, null, 2);
}

export async function importAllData(jsonStr: string): Promise<number> {
  const data = JSON.parse(jsonStr);
  if (data.accounts) {
    for (const acc of data.accounts) {
      await saveAccount(acc);
    }
  }
  let txCount = 0;
  if (data.transactions) {
    for (const [accountId, txs] of Object.entries(data.transactions)) {
      await saveTransactions(accountId, txs as Transaction[]);
      txCount += (txs as Transaction[]).length;
    }
  }
  if (data.mappings) await saveMappings(data.mappings);
  if (data.accountMeta) {
    for (const m of data.accountMeta) await saveAccountMeta(m);
  }
  if (data.entities) {
    for (const e of data.entities) await saveEntity(e);
  }
  if (data.expenseClasses) {
    for (const c of data.expenseClasses) await saveExpenseClass(c);
  }
  if (data.incomeSources) {
    for (const s of data.incomeSources) await saveIncomeSource(s);
  }
  if (data.rules) {
    for (const r of data.rules) await saveRule(r);
  }
  if (data.budgetPlans) {
    for (const p of data.budgetPlans) await saveBudgetPlan(p);
  }
  if (data.familyMembers) {
    for (const m of data.familyMembers) await saveFamilyMember(m);
  }
  if (data.categories) {
    await set(CATEGORIES_KEY, data.categories);
  }
  return txCount;
}

// Download transactions as CSV

export async function exportAsCSV(accountId?: string): Promise<string> {
  let txs: Transaction[];
  if (accountId) {
    txs = await getTransactions(accountId);
  } else {
    txs = await getAllTransactions();
  }

  const headers = ["Date", "Description", "Amount", "Type", "Category", "Entity", "Expense Category", "Channel", "Account ID"];
  const rows = txs.map((t) => [
    new Date(t.date).toLocaleDateString(),
    `"${t.name.replace(/"/g, '""')}"`,
    t.type === "debit" ? `-${t.amount}` : t.amount,
    t.type,
    t.category,
    t.entityId || "",
    t.expenseCategory || "",
    t.paymentChannel,
    t.accountId,
  ]);

  return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
}

// CRUD operations for individual transactions

export async function addTransaction(
  accountId: string,
  transaction: Transaction
): Promise<void> {
  const transactions = await getTransactions(accountId);
  transactions.unshift(transaction); // Add to beginning (newest first)
  await saveTransactions(accountId, transactions);
}

export async function updateTransaction(
  accountId: string,
  transactionId: string,
  updates: Partial<Transaction>
): Promise<void> {
  const transactions = await getTransactions(accountId);
  const idx = transactions.findIndex((t) => t.id === transactionId);
  if (idx >= 0) {
    transactions[idx] = { ...transactions[idx], ...updates };
    await saveTransactions(accountId, transactions);
  }
}

export async function deleteTransaction(
  accountId: string,
  transactionId: string
): Promise<void> {
  const transactions = await getTransactions(accountId);
  const filtered = transactions.filter((t) => t.id !== transactionId);
  await saveTransactions(accountId, filtered);
}

// Account Meta (owner, institution, account number)

export async function saveAccountMeta(meta: AccountMeta): Promise<void> {
  const all = await getAllAccountMeta();
  const idx = all.findIndex((m) => m.accountId === meta.accountId);
  if (idx >= 0) all[idx] = meta;
  else all.push(meta);
  await set(ACCOUNT_META_KEY, all);
}

export async function getAllAccountMeta(): Promise<AccountMeta[]> {
  return (await get<AccountMeta[]>(ACCOUNT_META_KEY)) || [];
}

export async function getAccountMeta(accountId: string): Promise<AccountMeta | undefined> {
  const all = await getAllAccountMeta();
  return all.find((m) => m.accountId === accountId);
}

// Business Entities (AIRE, LHB, Personal)

export async function saveEntity(entity: BusinessEntity): Promise<void> {
  const all = await getAllEntities();
  const idx = all.findIndex((e) => e.id === entity.id);
  if (idx >= 0) all[idx] = entity;
  else all.push(entity);
  await set(ENTITIES_KEY, all);
}

export async function getAllEntities(): Promise<BusinessEntity[]> {
  return (await get<BusinessEntity[]>(ENTITIES_KEY)) || [];
}

export async function deleteEntity(id: string): Promise<void> {
  const all = await getAllEntities();
  await set(ENTITIES_KEY, all.filter((e) => e.id !== id));
}

// Expense Classes (sub-categories within entities)

export async function saveExpenseClass(cls: ExpenseClass): Promise<void> {
  const all = await getAllExpenseClasses();
  const idx = all.findIndex((c) => c.id === cls.id);
  if (idx >= 0) all[idx] = cls;
  else all.push(cls);
  await set(CLASSES_KEY, all);
}

export async function getAllExpenseClasses(): Promise<ExpenseClass[]> {
  return (await get<ExpenseClass[]>(CLASSES_KEY)) || [];
}

export async function getClassesByEntity(entityId: string): Promise<ExpenseClass[]> {
  const all = await getAllExpenseClasses();
  return all.filter((c) => c.entityId === entityId);
}

export async function deleteExpenseClass(id: string): Promise<void> {
  const all = await getAllExpenseClasses();
  await set(CLASSES_KEY, all.filter((c) => c.id !== id));
}

// Income Sources

export async function saveIncomeSource(source: IncomeSource): Promise<void> {
  const all = await getAllIncomeSources();
  const idx = all.findIndex((s) => s.id === source.id);
  if (idx >= 0) all[idx] = source;
  else all.push(source);
  await set(INCOME_SOURCES_KEY, all);
}

export async function getAllIncomeSources(): Promise<IncomeSource[]> {
  return (await get<IncomeSource[]>(INCOME_SOURCES_KEY)) || [];
}

export async function deleteIncomeSource(id: string): Promise<void> {
  const all = await getAllIncomeSources();
  await set(INCOME_SOURCES_KEY, all.filter((s) => s.id !== id));
}

// Classification Rules - remembered entity assignments by transaction name

/** Normalize a transaction name for rule matching (lowercase, trim whitespace) */
export function normalizeRuleName(name: string): string {
  return name.toLowerCase().replace(/\s+/g, " ").trim();
}

export async function saveRule(rule: ClassificationRule): Promise<void> {
  const all = await getAllRules();
  // Upsert by normalized pattern
  const norm = normalizeRuleName(rule.pattern);
  const idx = all.findIndex((r) => normalizeRuleName(r.pattern) === norm);
  const saved = { ...rule, pattern: norm };
  if (idx >= 0) all[idx] = saved;
  else all.push(saved);
  await set(RULES_KEY, all);
}

export async function getAllRules(): Promise<ClassificationRule[]> {
  return (await get<ClassificationRule[]>(RULES_KEY)) || [];
}

export async function deleteRule(id: string): Promise<void> {
  const all = await getAllRules();
  await set(RULES_KEY, all.filter((r) => r.id !== id));
}

/**
 * Apply saved classification rules to a set of transactions.
 * Mutates transactions that match a rule (sets entityId and/or isTransfer).
 * Returns count of transactions modified.
 */
export async function applyRulesToTransactions(transactions: Transaction[]): Promise<number> {
  const rules = await getAllRules();
  if (rules.length === 0) return 0;

  // Build lookup map: normalized name → rule
  const ruleMap = new Map<string, ClassificationRule>();
  for (const rule of rules) {
    ruleMap.set(normalizeRuleName(rule.pattern), rule);
  }

  let count = 0;
  for (const tx of transactions) {
    const norm = normalizeRuleName(tx.name);
    const rule = ruleMap.get(norm);
    if (rule) {
      let changed = false;
      if (rule.entityId && !tx.entityId) {
        tx.entityId = rule.entityId;
        changed = true;
      }
      if (rule.expenseCategory && !tx.expenseCategory) {
        tx.expenseCategory = rule.expenseCategory;
        changed = true;
      }
      if (rule.incomeSourceId && !tx.incomeSourceId) {
        tx.incomeSourceId = rule.incomeSourceId;
      }
      if (rule.isTransfer !== undefined && !tx.isTransfer) {
        tx.isTransfer = rule.isTransfer;
      }
      if (changed) count++;
    }
  }
  return count;
}

// Budget Plans — spending targets, NOT actual transactions

export async function saveBudgetPlan(plan: StoredBudgetPlan): Promise<void> {
  const all = await getAllBudgetPlans();
  // If setting active, deactivate others
  if (plan.isActive) {
    for (const p of all) {
      if (p.id !== plan.id) p.isActive = false;
    }
  }
  const idx = all.findIndex((p) => p.id === plan.id);
  if (idx >= 0) all[idx] = plan;
  else all.push(plan);
  await set(BUDGET_PLAN_KEY, all);
}

export async function getAllBudgetPlans(): Promise<StoredBudgetPlan[]> {
  return (await get<StoredBudgetPlan[]>(BUDGET_PLAN_KEY)) || [];
}

export async function getActiveBudgetPlan(): Promise<StoredBudgetPlan | null> {
  const all = await getAllBudgetPlans();
  return all.find((p) => p.isActive) || all[0] || null;
}

export async function deleteBudgetPlan(id: string): Promise<void> {
  const all = await getAllBudgetPlans();
  await set(BUDGET_PLAN_KEY, all.filter((p) => p.id !== id));
}

// Family Members — household member management

export async function saveFamilyMember(member: FamilyMember): Promise<void> {
  const all = await getAllFamilyMembers();
  const idx = all.findIndex((m) => m.id === member.id);
  if (idx >= 0) all[idx] = member;
  else all.push(member);
  await set(FAMILY_KEY, all);
}

export async function getAllFamilyMembers(): Promise<FamilyMember[]> {
  return (await get<FamilyMember[]>(FAMILY_KEY)) || [];
}

export async function getFamilyMember(id: string): Promise<FamilyMember | undefined> {
  const all = await getAllFamilyMembers();
  return all.find((m) => m.id === id);
}

export async function deleteFamilyMember(id: string): Promise<void> {
  const all = await getAllFamilyMembers();
  await set(FAMILY_KEY, all.filter((m) => m.id !== id));
}

/** Find a family member by a known sourceAccount value (e.g. "Visa Debit 9491") */
export async function findMemberByAccount(sourceAccount: string): Promise<FamilyMember | undefined> {
  if (!sourceAccount) return undefined;
  const all = await getAllFamilyMembers();
  const normalized = sourceAccount.toLowerCase().trim();
  return all.find((m) =>
    m.knownAccounts?.some((a) => a.toLowerCase().trim() === normalized)
  );
}

// ─── Persist Transactions (bulk save back to per-account stores) ─

/**
 * Save a flat array of transactions back to IndexedDB, grouped by accountId.
 * Used by renameCategory/deleteCategory to cascade changes.
 */
async function persistTransactions(transactions: Transaction[]): Promise<void> {
  const byAccount = new Map<string, Transaction[]>();
  for (const tx of transactions) {
    const key = tx.accountId || "";
    if (!byAccount.has(key)) byAccount.set(key, []);
    byAccount.get(key)!.push(tx);
  }
  for (const entry of Array.from(byAccount.entries())) {
    if (entry[0]) {
      await set(TX_PREFIX + entry[0], entry[1]);
    }
  }
}

// ─── Expense Category CRUD ───────────────────────────────────────

import { DEFAULT_CATEGORIES } from "./types";

type CategoryMap = Record<string, string[]>;

/** Get categories for an entity, seeding from defaults if first use */
export async function getCategories(entityId: string): Promise<string[]> {
  const all = await get<CategoryMap>(CATEGORIES_KEY) || {};
  if (!all[entityId]) {
    // Seed from defaults
    all[entityId] = DEFAULT_CATEGORIES[entityId] || DEFAULT_CATEGORIES.personal;
    await set(CATEGORIES_KEY, all);
  }
  return all[entityId];
}

/** Get all entity category maps */
export async function getAllCategories(): Promise<CategoryMap> {
  const all = await get<CategoryMap>(CATEGORIES_KEY) || {};
  // Seed any missing entities
  let changed = false;
  for (const eid of Object.keys(DEFAULT_CATEGORIES)) {
    if (!all[eid]) {
      all[eid] = DEFAULT_CATEGORIES[eid];
      changed = true;
    }
  }
  if (changed) await set(CATEGORIES_KEY, all);
  return all;
}

/** Add a new category to an entity */
export async function addCategory(entityId: string, name: string): Promise<void> {
  const all = await get<CategoryMap>(CATEGORIES_KEY) || {};
  if (!all[entityId]) all[entityId] = DEFAULT_CATEGORIES[entityId] || DEFAULT_CATEGORIES.personal;
  if (!all[entityId].includes(name)) {
    all[entityId].push(name);
    await set(CATEGORIES_KEY, all);
  }
}

/** Rename a category — cascades to transactions and rules */
export async function renameCategory(
  entityId: string,
  oldName: string,
  newName: string
): Promise<{ txUpdated: number; rulesUpdated: number }> {
  // Update category list
  const all = await get<CategoryMap>(CATEGORIES_KEY) || {};
  if (all[entityId]) {
    const idx = all[entityId].indexOf(oldName);
    if (idx >= 0) all[entityId][idx] = newName;
    await set(CATEGORIES_KEY, all);
  }

  // Cascade to transactions
  const allTx = await getAllTransactions();
  let txUpdated = 0;
  const updated = allTx.map((tx) => {
    if (tx.expenseCategory === oldName && (!entityId || tx.entityId === entityId)) {
      txUpdated++;
      return { ...tx, expenseCategory: newName };
    }
    return tx;
  });
  if (txUpdated > 0) await persistTransactions(updated);

  // Cascade to rules
  const rules = await getAllRules();
  let rulesUpdated = 0;
  const updatedRules = rules.map((r) => {
    if (r.expenseCategory === oldName && (!entityId || r.entityId === entityId)) {
      rulesUpdated++;
      return { ...r, expenseCategory: newName };
    }
    return r;
  });
  if (rulesUpdated > 0) await set(RULES_KEY, updatedRules);

  return { txUpdated, rulesUpdated };
}

/** Delete a category — clears expenseCategory on affected transactions and rules */
export async function deleteCategory(
  entityId: string,
  name: string
): Promise<{ txCleared: number; rulesCleared: number }> {
  // Remove from list
  const all = await get<CategoryMap>(CATEGORIES_KEY) || {};
  if (all[entityId]) {
    all[entityId] = all[entityId].filter((c) => c !== name);
    await set(CATEGORIES_KEY, all);
  }

  // Clear on transactions
  const allTx = await getAllTransactions();
  let txCleared = 0;
  const updated = allTx.map((tx) => {
    if (tx.expenseCategory === name && (!entityId || tx.entityId === entityId)) {
      txCleared++;
      return { ...tx, expenseCategory: undefined };
    }
    return tx;
  });
  if (txCleared > 0) await persistTransactions(updated);

  // Clear on rules
  const rules = await getAllRules();
  let rulesCleared = 0;
  const updatedRules = rules.map((r) => {
    if (r.expenseCategory === name && (!entityId || r.entityId === entityId)) {
      rulesCleared++;
      return { ...r, expenseCategory: undefined };
    }
    return r;
  });
  if (rulesCleared > 0) await set(RULES_KEY, updatedRules);

  return { txCleared, rulesCleared };
}
